import { useSessionKey } from "@auth/sessionStore";
import { BoardFrame, BoardLanes, BoardSkeleton } from "@board/BoardLanes";
import type { BoardVisit } from "@board/boardVisit";
import { ClinicRoster } from "@board/ClinicRoster";
import { type BoardCardGroup, dvmNames } from "@board/groupBoardVisits";
import { boardName, VisitCard } from "@board/VisitCard";
import { useLiveClinicMap } from "@clinicMap/clinicMapQueries";
import { LiveClinicMap } from "@clinicMap/LiveClinicMap";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { BOARD_VIEW_LABEL } from "@displays/displaysClient";
import { usePatientSprites } from "@patients/signalment";
import { RoomWho } from "@rooms/RoomConfirmControl";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import { BoardLane, PanelError } from "@scribenote/brian-design-system";
import { VisitProgressControl } from "@visits/VisitProgressControl";
import {
  isConfirmedIn,
  isVisitCancelled,
  type TodayVisit,
} from "@visits/visitsClient";
import { useBoardVisits } from "@visits/visitsQueries";
import { useMemo } from "react";

interface StaffBoardVisit extends BoardVisit {
  source: TodayVisit;
}

const toBoardVisit = (
  visit: TodayVisit,
  roomNames: ReadonlyMap<string, string>,
): StaffBoardVisit => ({
  id: visit.id,
  patientId: visit.patientId,
  patientName: visit.patientName,
  name: boardName(visit),
  householdId: visit.householdId,
  householdLabel: visit.householdLabel,
  visitType: visit.visitType,
  flow: visit.flow,
  position: visit.position,
  progressAt: visit.progressAt,
  roomId: visit.currentRoomUuid,
  roomName: visit.currentRoomUuid
    ? (roomNames.get(visit.currentRoomUuid) ?? null)
    : null,
  dvmName: visit.assignedUserName,
  scheduledStart: visit.scheduledStart,
  scheduledEnd: visit.scheduledEnd,
  createdAt: visit.createdAt,
  board: visit.board,
  source: visit,
});

const useStaffBoardVisits = (): StaffBoardVisit[] => {
  const sessionKey = useSessionKey();
  const board = useBoardVisits(sessionKey);
  const { data: rooms } = useRoomsListQuery(sessionKey, undefined, true);

  return useMemo(() => {
    const roomNames = new Map(
      (rooms ?? []).map((room) => [room.id, room.name]),
    );
    return board
      .filter((visit) => !isVisitCancelled(visit))
      .map((visit) => toBoardVisit(visit, roomNames));
  }, [board, rooms]);
};

const StaffVisitCard = ({
  card,
  now,
  onOpenPatient,
  readOnly,
}: {
  card: BoardCardGroup<StaffBoardVisit>;
  now: number;
  onOpenPatient?: (patientId: string) => void;
  readOnly: boolean;
}) => {
  const { visits } = card;
  const household = visits.length > 1;
  const sprites = usePatientSprites(visits.map((p) => p.visit.patientId));
  // The room chip speaks for a pet still to confirm, whose "Confirm both" covers the rest.
  const { visit } =
    visits.find(
      (p) => p.visit.roomId && !isConfirmedIn(p.visit.source, p.visit.roomId),
    ) ?? visits[0];
  const dvms = dvmNames(visits.map((p) => p.visit.dvmName));
  return (
    <div className="flex flex-col gap-1">
      <VisitCard
        card={card}
        now={now}
        sprites={sprites}
        who={
          <RoomWho
            visit={visit.source}
            name={visit.name}
            roomName={visit.roomName}
            rest={dvms.length > 0 ? dvms.join(" · ") : null}
            readOnly={readOnly}
          />
        }
        onOpenPatient={onOpenPatient}
      />
      {!readOnly &&
        visits.map((p) => (
          <VisitProgressControl
            key={p.visit.id}
            visit={p.visit.source}
            label={household ? p.visit.patientName : undefined}
          />
        ))}
    </div>
  );
};

const StaffLanes = ({
  onOpenPatient,
  readOnly,
}: {
  onOpenPatient?: (patientId: string) => void;
  readOnly: boolean;
}) => {
  const visits = useStaffBoardVisits();
  return (
    <BoardLanes
      visits={visits}
      renderCard={(card, now) => (
        <StaffVisitCard
          card={card}
          now={now}
          onOpenPatient={onOpenPatient}
          readOnly={readOnly}
        />
      )}
    />
  );
};

const LiveMapLane = () => {
  const { data: live } = useLiveClinicMap(useSessionKey());
  if (!live || live.revision === 0) return null;
  return (
    <BoardLane title={BOARD_VIEW_LABEL.map}>
      <LiveClinicMap live={live} className="max-w-5xl" />
    </BoardLane>
  );
};

/** The board from the staff visit feeds; a paired board display reads its own feed (`ClinicBoardDisplay`). */
export const ClinicBoard = ({
  onOpenPatient,
  readOnly = false,
}:
  | { onOpenPatient: (patientId: string) => void; readOnly?: false }
  | {
      onOpenPatient?: undefined;
      /** Cards don't open and carry no room or progress controls. */
      readOnly: true;
    }) => (
  <BoardFrame>
    <AsyncBoundary
      skeleton={<BoardSkeleton />}
      fallback={(retry) => (
        <PanelError label="the clinic board" onRetry={retry} />
      )}
    >
      <StaffLanes onOpenPatient={onOpenPatient} readOnly={readOnly} />
    </AsyncBoundary>
    {!readOnly && <LiveMapLane />}
    <ClinicRoster />
  </BoardFrame>
);
